import React, { FC } from 'react';

import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Link } from '@resideo/blueprint-react';

import { formatFileSize, formatDate } from 'utils/common';
import PageLayout from 'components/common/PageLayout';
import PageLoading from 'components/common/PageLoading';
import Space from 'components/common/Space';
import Typography from 'components/common/Typography';

import { useDownloadDataRequests } from 'hooks/useRequestMyData';
import { useRisClient } from 'hooks/useRisClient';

const { Text } = Typography;

const RequestTitle = styled(Text)`
  font-size: 1rem;
`;

const RequestInfo = styled(Text)`
  color: ${({ theme }) => theme.colors.faded};
  font-size: 1rem;
`;

const DownloadDataScreen: FC = () => {
  const { t } = useTranslation();

  const { data: downloadRequests, isLoading } = useDownloadDataRequests();
  const { client } = useRisClient();

  const downloadBlob = (blob: Blob, fileName: string) => {
    const tempObjectUrl = window.URL.createObjectURL(blob);
    const tempLink = document.createElement('a');
    tempLink.href = tempObjectUrl;
    tempLink.download = fileName;
    tempLink.click();
    tempLink.remove();
    window.URL.revokeObjectURL(tempObjectUrl);
  };

  const formatDateForFilename = (timestamp: number = new Date().getTime()) => {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}${month}${day}_${hours}${minutes}${seconds}`;
  };

  const handleDownloadFile = async (
    requestId: string,
    isFullDataRequest: boolean
  ) => {
    try {
      // Use the RIS client which handles authentication and base URL automatically
      const blob =
        await client.reports.reportsDownloadDataRequestFile(requestId);

      if (!(blob instanceof Blob)) {
        throw new Error(
          `Download response is not a Blob for requestId=${requestId}`
        );
      }

      const formattedDate = formatDateForFilename();
      const fileName = isFullDataRequest
        ? `Full_Data_Request_${formattedDate}.zip`
        : `Single_Data_Request_${formattedDate}.zip`;
      downloadBlob(blob, fileName);
    } catch (error) {
      throw new Error(
        `Download failed for requestId=${requestId}: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  };

  return (
    <PageLayout
      data-test-download-data
      title={t('privacy.dataRequests.downloadData')}>
      <Text>{t('privacy.dataRequests.download.description')}</Text>

      <Space size={32} />

      {isLoading ? (
        <PageLoading />
      ) : (
        <Box>
          {downloadRequests?.length ? (
            downloadRequests.map(downloadRequest => (
              <Flex
                data-test-download-row
                key={downloadRequest.id}
                alignItems='center'
                justifyContent='space-between'
                marginBottom='32px'>
                <Flex flexDirection='column' style={{ gap: '6px' }}>
                  <RequestTitle>
                    {downloadRequest.name}{' '}
                    {t('privacy.dataRequests.download.request')}
                  </RequestTitle>

                  <RequestInfo>
                    {t('privacy.dataRequests.download.fileSize')}:{' '}
                    {formatFileSize(downloadRequest.size)}
                  </RequestInfo>

                  <RequestInfo>
                    {formatDate(downloadRequest.created_at)}
                  </RequestInfo>
                </Flex>

                <Link
                  onClick={() =>
                    handleDownloadFile(
                      downloadRequest.id,
                      downloadRequest.isFullDataRequest
                    )
                  }>
                  {t('privacy.dataRequests.download.action')}
                </Link>
              </Flex>
            ))
          ) : (
            <Text>{t('privacy.dataRequests.download.noData')}</Text>
          )}
        </Box>
      )}
    </PageLayout>
  );
};

export default DownloadDataScreen;
